import { useCallback, useRef, useState } from 'react';
import { ModalLayout, SearchableSelect, TextArea, Button, useToast } from '@aimsos/ui';
import { useTranslation } from '@aimsos/i18n';
import EscalateEventSummary from './EscalateEventSummary.jsx';
import ValueSelectableChip from './ValueSelectableChip.jsx';
import { ESCALATION_REASONS } from '../../constants/people.js';
import { useWorkspaceUsers } from '../../hooks/useWorkspaceUsers.js';
import { useEscalateAction } from '../../hooks/useEscalateAction.js';
import { ArrowUp } from 'lucide-react';

const LABEL_CLASS = 'mb-2 text-xs font-semibold uppercase tracking-wide text-content-faint';

/**
 * The DS chip ships as a small pill with a 2px light border; reason chips follow
 * the prototype's badge radius and hairline edge, so the shape is overridden
 * here instead of forking the component.
 */
const reasonChipClassName = (selected) =>
  `!rounded-[8px] !border ${selected ? '!border-transparent' : '!border-edge'}`;

/** Priority keeps the DS pill; High escalates the selected tone to coral. */
const HIGH_PRIORITY_SELECTED_CLASS =
  '!bg-rose-500 hover:!bg-rose-600 dark:!bg-rose-500 dark:hover:!bg-rose-600';

const PRIORITIES = [
  { value: 'normal', labelKey: 'escalate.priorityNormal' },
  { value: 'high', labelKey: 'escalate.priorityHigh' },
];

/**
 * Escalate an event to a single person: say why, pick who should take it, and
 * explain what you need. Priority is optional.
 *
 * Escalate is entity-keyed (CT-5094): it CREATES or reassigns an open task via
 * the entity escalate endpoints (NOT task/reassign). The backend takes ONE
 * `assigneeUuid`, so the picker is single-select even though the prototype shows
 * multiple collaborators. For a `task` event the owner updates optimistically;
 * for an entity event a task is created. See {@link useEscalateAction}.
 */
function EscalateModal({ isOpen, event, onClose }) {
  const { t } = useTranslation('work-queues-app');
  const toast = useToast();
  const escalate = useEscalateAction();
  const [reason, setReason] = useState(null);
  const [assignee, setAssignee] = useState(null);
  const [message, setMessage] = useState('');
  const [priority, setPriority] = useState('normal');
  const [submitting, setSubmitting] = useState(false);
  const [assigneeSearch, setAssigneeSearch] = useState('');
  const submittingRef = useRef(false);
  // Real workspace directory (CT-5094) — same picker source as Reassign.
  // `[assignee?.id]` (selectedIds) keeps a just-picked assignee from vanishing
  // from `options` when the search that surfaced them clears and refetches.
  const { users, loading: usersLoading, error: usersError } = useWorkspaceUsers(
    isOpen,
    assigneeSearch,
    assignee?.id ? [assignee.id] : undefined,
  );

  const canSubmit = Boolean(reason) && Boolean(assignee) && message.trim().length > 0 && !submitting;

  const handleMessageChange = useCallback((changeEvent) => {
    setMessage(changeEvent.target.value);
  }, []);

  // The modal stays mounted (EventActionModals renders it unconditionally), so
  // `useState` survives a close. Reset on every close path (Cancel, overlay, Esc,
  // X) so a reopen for a different event never shows the previous selection.
  const resetForm = useCallback(() => {
    setReason(null);
    setAssignee(null);
    setAssigneeSearch('');
    setMessage('');
    setPriority('normal');
  }, []);

  const handleClose = useCallback(() => {
    resetForm();
    onClose();
  }, [resetForm, onClose]);

  const handleSubmit = useCallback(async () => {
    if (!canSubmit || submittingRef.current) return;
    submittingRef.current = true;
    setSubmitting(true);
    try {
      const ok = await escalate(event, { assignee, reason, priority, message });
      if (!ok) return;
      toast.success({
        title: t('escalate.sentTitle'),
        message: t('escalate.sentMessage', { assignee: assignee.name }),
      });
      resetForm();
      onClose();
    } finally {
      submittingRef.current = false;
      setSubmitting(false);
    }
  }, [assignee, canSubmit, escalate, event, message, onClose, priority, reason, resetForm, t, toast]);

  if (!isOpen) return null;

  return (
    <ModalLayout
      isOpen={isOpen}
      onClose={handleClose}
      useBodyPortal
      maxWidthClassName="max-w-xl"
      panelClassName="max-h-[min(90vh,44rem)]"
      titlePrefix={
        <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-amber-500/15 text-amber-500">
          <ArrowUp size={18} />
        </span>
      }
      title={t('escalate.title')}
      subtitle={t('escalate.subtitle')}
    >
      <div className="flex min-h-0 flex-1 flex-col gap-5 overflow-y-auto px-6 py-4 scrollbar-aimsos-auto-sm">
        <EscalateEventSummary event={event} />

        <div role="group" aria-labelledby="escalate-reason-label">
          <p id="escalate-reason-label" className={LABEL_CLASS}>
            {t('escalate.reasonLabel')}
            <span className="ml-0.5 text-rose-500">*</span>
          </p>
          <div className="flex flex-wrap gap-2">
            {ESCALATION_REASONS.map((option) => (
              <ValueSelectableChip
                key={option.value}
                value={option.value}
                size="m"
                className={reasonChipClassName(reason === option.value)}
                selected={reason === option.value}
                onSelect={setReason}
              >
                {t(option.labelKey)}
              </ValueSelectableChip>
            ))}
          </div>
        </div>

        <div role="group" aria-labelledby="escalate-assignee-label">
          <p id="escalate-assignee-label" className={LABEL_CLASS}>
            {t('escalate.assigneeLabel')}
            <span className="ml-0.5 text-rose-500">*</span>
          </p>
          <SearchableSelect
            options={users}
            value={assignee?.id ?? null}
            onSelect={setAssignee}
            onChange={setAssigneeSearch}
            showAvatar={false}
            ariaLabel={t('escalate.assigneeLabel')}
            disabled={usersLoading || Boolean(usersError)}
            placeholder={usersLoading ? t('escalate.assigneeLoading') : t('escalate.assigneePlaceholder')}
            searchPlaceholder={t('escalate.assigneeSearch')}
          />
          {usersError && (
            <p className="mt-1.5 text-xs text-rose-500">{t('escalate.assigneeLoadError')}</p>
          )}
          {!usersLoading && !usersError && users.length === 0 && (
            <p className="mt-1.5 text-xs text-content-faint">{t('escalate.assigneeEmpty')}</p>
          )}
        </div>

        <div>
          <label htmlFor="escalate-message" className={`block ${LABEL_CLASS}`}>
            {t('escalate.messageLabel')}
            <span className="ml-0.5 text-rose-500">*</span>
          </label>
          <TextArea
            id="escalate-message"
            rows={4}
            value={message}
            onChange={handleMessageChange}
            placeholder={t('escalate.messagePlaceholder')}
          />
        </div>

        <div role="group" aria-labelledby="escalate-priority-label">
          <p id="escalate-priority-label" className={LABEL_CLASS}>
            {t('escalate.priorityLabel')}
            <span className="ml-1 font-normal normal-case text-content-faint">
              {t('escalate.optional')}
            </span>
          </p>
          <div className="flex flex-wrap gap-2">
            {PRIORITIES.map((option) => (
              <ValueSelectableChip
                key={option.value}
                value={option.value}
                size="m"
                className={
                  priority === option.value && option.value === 'high'
                    ? HIGH_PRIORITY_SELECTED_CLASS
                    : ''
                }
                selected={priority === option.value}
                onSelect={setPriority}
              >
                {t(option.labelKey)}
              </ValueSelectableChip>
            ))}
          </div>
        </div>
      </div>

      <div className="flex shrink-0 justify-end gap-2 border-t border-edge px-6 py-4">
        <Button variant="secondary" size="sm" onClick={handleClose}>
          {t('actions.cancel')}
        </Button>
        <Button
          variant="primary"
          size="sm"
          leftIcon={ArrowUp}
          disabled={!canSubmit}
          onClick={handleSubmit}
        >
          {t('escalate.submit')}
        </Button>
      </div>
    </ModalLayout>
  );
}

export default EscalateModal;
